// components/ProductTable.tsx - Enhanced version with global products
import React from "react";
import { OrderDetail } from "../hooks/types";
import { roundValue } from "../../home/utils/formatters";

interface ProductTableProps {
  orderData: OrderDetail;
}

const ProductTable: React.FC<ProductTableProps> = ({ orderData }) => {
  const cars = orderData?.cars || [];
  const globalProducts = orderData?.Products || [];
  const isMultiCar = cars.length > 1;

  // Get products for each car from services[0].productDetails
  const getCarProducts = (car: any) => {
    if (car.services && car.services.length > 0 && car.services[0].productDetails) {
      return car.services[0].productDetails;
    }
    return [];
  };

  // Get global products not assigned to specific cars
  const getUnassignedProducts = () => {
    const assignedProductNames = new Set();
    
    // Collect all product names assigned to cars
    cars.forEach(car => {
      const products = getCarProducts(car);
      products.forEach((product: any) => {
        assignedProductNames.add(product.product);
      });
    });
    
    // Filter global products not assigned to any car
    return globalProducts.filter(product => 
      !assignedProductNames.has(product.name)
    );
  };

  // Calculate subtotal for a car
  const calculateCarSubtotal = (car: any) => {
    const products = getCarProducts(car);
    return products.reduce((total: number, product: any) => {
      const price = typeof product.price === 'string' 
        ? parseFloat(product.price) 
        : product.price || 0;
      return total + price;
    }, 0);
  };

  const unassignedProducts = getUnassignedProducts();

  return (
    <div className="mb-4 text-sm">
      {/* Table Header */}
      <div className="flex justify-between font-bold border-b border-black pb-1 mb-2">
        <div className="w-3/5">
          Description <br /> وصف
        </div>
        <div className="w-1/5">
          Qty <br /> كمية
        </div>
        <div className="w-1/5 text-right">
          Amount <br /> المبلغ
        </div>
      </div>

      {/* Multi-Car Order Header */}
      {isMultiCar && (
        <div className="text-center font-bold mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
          MULTI-CAR ORDER - {cars.length} VEHICLES
        </div>
      )}

      {/* Unassigned Products (if any) */}
      {unassignedProducts.length > 0 && (
        <div className="mb-4">
          <div className="font-bold mb-2 border border-black rounded px-2 py-1 bg-gray-50">
            🚗 General Services
          </div>
          {unassignedProducts.map((product, index) => (
            <div key={`unassigned-${index}`} className="flex justify-between mb-1">
              <div className="w-3/5">{product.name}</div>
              <div className="w-1/5 text-center">{product.quantity}</div>
              <div className="w-1/5 text-right">
                {roundValue(typeof product.totalPrice === 'string' 
                  ? parseFloat(product.totalPrice) 
                  : product.totalPrice)} ر.ق
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cars & Products */}
      {cars.map((car, carIndex) => {
        const products = getCarProducts(car);
        const carSubtotal = calculateCarSubtotal(car);

        return (
          <div key={`car-${carIndex}`} className="mb-4">
            {/* Car Header */}
            <div className="font-bold mb-2 border border-black rounded px-2 py-1 bg-gray-50">
              🚗 Car {carIndex + 1}: {car.carNumber}
            </div>

            {/* Product Rows for this car */}
            {products.length > 0 ? (
              products.map((product: any, productIndex: number) => (
                <div key={`product-${productIndex}`} className="flex justify-between mb-1">
                  <div className="w-3/5">{product.product}</div>
                  <div className="w-1/5 text-center">1</div>
                  <div className="w-1/5 text-right">
                    {roundValue(typeof product.price === 'string' 
                      ? parseFloat(product.price) 
                      : product.price)} ر.ق
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-2 text-gray-500 italic">
                No specific products listed
              </div>
            )}

            {/* Car Subtotal */}
            {products.length > 0 && (
              <div className="flex justify-between mt-2 border-t border-dashed pt-2 font-bold">
                <div>Car {carIndex + 1} Subtotal:</div>
                <div className="text-right">{roundValue(carSubtotal)} ر.ق</div>
              </div>
            )}

            {/* Separator between cars (except last one) */}
            {carIndex < cars.length - 1 && (
              <div className="border-b border-dashed border-gray-300 my-3"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProductTable;